import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, Bell, Edit2, Trash2, Plus, Save, X, Eye, Settings, MessageSquare } from 'lucide-react';

const API_URL = getApiBaseUrl();

const AdminNotificationTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [arrearsConfig, setArrearsConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    trigger: 'rent_overdue_day_1',
    channel: 'email',
    subject: '',
    body_template: '',
    enabled: true,
    priority: 5,
    delay_minutes: 0
  });

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [templatesRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/notifications/templates`, { headers }),
        fetch(`${API_URL}/api/admin/notifications/ai-arrears-config`, { headers })
      ]);
      
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (configRes.ok) setArrearsConfig(await configRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template) => {
    try {
      const token = getToken();
      const isNew = !template.id;
      const url = isNew 
        ? `${API_URL}/api/admin/notifications/templates`
        : `${API_URL}/api/admin/notifications/templates/${template.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(template)
      });
      
      if (response.ok) {
        toast.success(isNew ? 'Template created' : 'Template updated');
        fetchData();
        setShowCreateModal(false);
        setEditingTemplate(null);
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Template deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const saveArrearsConfig = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/ai-arrears-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(arrearsConfig)
      });
      
      if (response.ok) {
        toast.success('AI Arrears config saved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save config');
    }
  };

  const seedDefaults = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const triggerOptions = [
    { value: 'rent_overdue_day_1', label: 'Rent Overdue - Day 1' },
    { value: 'rent_overdue_day_7', label: 'Rent Overdue - Day 7' },
    { value: 'rent_overdue_day_14', label: 'Rent Overdue - Day 14' },
    { value: 'rent_overdue_day_21', label: 'Rent Overdue - Day 21' },
    { value: 'rent_overdue_day_30', label: 'Rent Overdue - Day 30' },
    { value: 'application_received', label: 'Application Received' },
    { value: 'application_approved', label: 'Application Approved' },
    { value: 'reference_request', label: 'Reference Request' },
    { value: 'guarantor_required', label: 'Guarantor Required' },
    { value: 'certificate_expiring_30', label: 'Certificate Expiring - 30 days' },
    { value: 'certificate_expiring_14', label: 'Certificate Expiring - 14 days' },
    { value: 'maintenance_created', label: 'Maintenance Created' },
    { value: 'viewing_confirmed', label: 'Viewing Confirmed' }
  ];

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Notification Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDefaults}>
            Seed Defaults
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          className={`px-4 py-2 ${activeTab === 'templates' ? 'border-b-2 border-blue-600 text-blue-600' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Email Templates
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'arrears' ? 'border-b-2 border-blue-600 text-blue-600' : ''}`}
          onClick={() => setActiveTab('arrears')}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          AI Arrears Config
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid gap-4">
          {templates.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              No templates yet. Click &quot;Seed Defaults&quot; to create default templates.
            </Card>
          ) : (
            templates.map(template => (
              <Card key={template.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{template.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs ${template.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {template.enabled ? 'Active' : 'Disabled'}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                        {template.channel}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Trigger: {template.trigger}</p>
                    {template.subject && (
                      <p className="text-sm text-gray-500 mt-1">Subject: {template.subject}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingTemplate(template)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTemplate(template.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* AI Arrears Config Tab */}
      {activeTab === 'arrears' && arrearsConfig && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">AI Arrears Workflow Configuration</h2>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Tone */}
            <div>
              <label className="block text-sm font-medium mb-2">Communication Tone</label>
              <select
                value={arrearsConfig.tone}
                onChange={(e) => setArrearsConfig({...arrearsConfig, tone: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="friendly">Friendly</option>
                <option value="professional">Professional</option>
                <option value="firm">Firm</option>
                <option value="formal">Formal</option>
              </select>
            </div>

            {/* Aggressiveness */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Aggressiveness Level: {arrearsConfig.aggressiveness}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={arrearsConfig.aggressiveness}
                onChange={(e) => setArrearsConfig({...arrearsConfig, aggressiveness: parseInt(e.target.value)})}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Gentle</span>
                <span>Aggressive</span>
              </div>
            </div>

            {/* Escalation Speed */}
            <div>
              <label className="block text-sm font-medium mb-2">Escalation Speed</label>
              <select
                value={arrearsConfig.escalation_speed}
                onChange={(e) => setArrearsConfig({...arrearsConfig, escalation_speed: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="slow">Slow</option>
                <option value="standard">Standard</option>
                <option value="fast">Fast</option>
              </select>
            </div>

            {/* Max Auto Reminders */}
            <div>
              <label className="block text-sm font-medium mb-2">Max Auto Reminders</label>
              <input
                type="number"
                value={arrearsConfig.max_auto_reminders}
                onChange={(e) => setArrearsConfig({...arrearsConfig, max_auto_reminders: parseInt(e.target.value)})}
                className="w-full p-2 border rounded"
                min="1"
                max="10"
              />
            </div>

            {/* Physical Letter Threshold */}
            <div>
              <label className="block text-sm font-medium mb-2">Physical Letter After (days)</label>
              <input
                type="number"
                value={arrearsConfig.physical_letter_threshold_days}
                onChange={(e) => setArrearsConfig({...arrearsConfig, physical_letter_threshold_days: parseInt(e.target.value)})}
                className="w-full p-2 border rounded"
              />
            </div>

            {/* Legal Action Threshold */}
            <div>
              <label className="block text-sm font-medium mb-2">Legal Action After (days)</label>
              <input
                type="number"
                value={arrearsConfig.legal_action_threshold_days}
                onChange={(e) => setArrearsConfig({...arrearsConfig, legal_action_threshold_days: parseInt(e.target.value)})}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={arrearsConfig.auto_generate_messages}
                onChange={(e) => setArrearsConfig({...arrearsConfig, auto_generate_messages: e.target.checked})}
              />
              Auto-generate AI messages
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={arrearsConfig.include_payment_plan_offer}
                onChange={(e) => setArrearsConfig({...arrearsConfig, include_payment_plan_offer: e.target.checked})}
              />
              Include payment plan offers
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={arrearsConfig.mention_legal_consequences}
                onChange={(e) => setArrearsConfig({...arrearsConfig, mention_legal_consequences: e.target.checked})}
              />
              Mention legal consequences
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={arrearsConfig.require_landlord_approval_for_legal}
                onChange={(e) => setArrearsConfig({...arrearsConfig, require_landlord_approval_for_legal: e.target.checked})}
              />
              Require landlord approval for legal action
            </label>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={saveArrearsConfig}>
              <Save className="w-4 h-4 mr-2" /> Save Configuration
            </Button>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={editingTemplate?.name || newTemplate.name}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, name: e.target.value})
                    : setNewTemplate({...newTemplate, name: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Trigger</label>
                  <select
                    value={editingTemplate?.trigger || newTemplate.trigger}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, trigger: e.target.value})
                      : setNewTemplate({...newTemplate, trigger: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    {triggerOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Channel</label>
                  <select
                    value={editingTemplate?.channel || newTemplate.channel}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, channel: e.target.value})
                      : setNewTemplate({...newTemplate, channel: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="in_app">In-App</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Subject (for email)</label>
                <input
                  type="text"
                  value={editingTemplate?.subject || newTemplate.subject}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, subject: e.target.value})
                    : setNewTemplate({...newTemplate, subject: e.target.value})}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., Rent Payment Reminder - {{property_address}}"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Body Template</label>
                <textarea
                  value={editingTemplate?.body_template || newTemplate.body_template}
                  onChange={(e) => editingTemplate 
                    ? setEditingTemplate({...editingTemplate, body_template: e.target.value})
                    : setNewTemplate({...newTemplate, body_template: e.target.value})}
                  className="w-full p-2 border rounded h-48 font-mono text-sm"
                  placeholder="Use {{variable}} for merge fields"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: tenant_name, property_address, rent_amount, due_date, total_outstanding
                </p>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingTemplate?.enabled ?? newTemplate.enabled}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, enabled: e.target.checked})
                      : setNewTemplate({...newTemplate, enabled: e.target.checked})}
                  />
                  Enabled
                </label>
                <div>
                  <label className="text-sm font-medium mr-2">Priority:</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editingTemplate?.priority || newTemplate.priority}
                    onChange={(e) => editingTemplate 
                      ? setEditingTemplate({...editingTemplate, priority: parseInt(e.target.value)})
                      : setNewTemplate({...newTemplate, priority: parseInt(e.target.value)})}
                    className="w-16 p-1 border rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}>
                  Cancel
                </Button>
                <Button onClick={() => saveTemplate(editingTemplate || newTemplate)}>
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationTemplates;
